import {
  REPARTITION_CA_DATA,
  RepartitionCaDetail,
} from "../../../dashboard/models/data.model";
import { StatsTiers } from "../../models/stats-tiers.model";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import type { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";

/**
 * Transforme `StatsTiers` (facturesLignes + lotCosts) en `REPARTITION_CA_DATA`.
 *
 * Reproduit côté front le calcul du back `WidgetService.getDataRepartitionCA()`
 * mais pour un tiers unique : on a déjà la valeur HT par ligne de facture et
 * les coûts profonds par lot.
 */
export function repartitionCaFromStats(
  stats: StatsTiers | null | undefined,
  articleInfosFacade: ArticlesInfosFacadeService,
): REPARTITION_CA_DATA | null {
  if (!stats?.facturesLignes?.length) return null;

  // Map lotId → coûts profonds (mat, mo)
  const lotCostMap = new Map<string, { mat: number; mo: number }>();
  for (const lc of stats.lotCosts ?? []) {
    lotCostMap.set(lc.lotId, {
      mat: parseNumber(lc.coutMat),
      mo: parseNumber(lc.coutMO),
    });
  }

  const clientDenomination = stats.tiers?.denomination ?? "Client";

  const details: RepartitionCaDetail[] = [];
  const clientMap = new Map<string, number>();
  const articleMap = new Map<string, number>();
  const familleMap = new Map<string, number>();
  const groupeTiersMap = new Map<string, number>();
  let total = 0;

  for (const fl of stats.facturesLignes as any[]) {
    const articleId: string =
      fl?.articleId ??
      fl?.article?.id ??
      fl?.commandeClientLigne?.articleId ??
      fl?.commandeClientLigne?.article?.id ??
      "Inconnu";

    const ai = articleInfosFacade.getByArticleId(articleId)?.();
    const articleName: string = ai?.denomination ?? "Sans article";
    const familleName: string =
      (ai?.famille as any)?.denomination ?? "Non classé";
    const uniteDenom: string = (ai?.unite as any)?.denomination ?? "u";
    const uniteVente: string = uniteDenom;
    const uniteGestion: string = uniteDenom;

    const coefUG =
      parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
    const qty = parseNumber(fl?.quantite ?? 0);
    const pu = parseNumber(fl?.prix ?? 0);
    const remise = parseNumber(fl?.remise ?? 0);
    const montantHT = qty * pu - remise;
    if (montantHT <= 0) continue;

    const qtyUG = qty * coefUG;
    const lotId: string | undefined = fl?.lotId ?? fl?.lot?.id;
    const lotCost = lotId ? lotCostMap.get(lotId) : null;
    const coutMateriel = qtyUG * (lotCost?.mat ?? 0);
    const coutMO = qtyUG * (lotCost?.mo ?? 0);
    const coutUnitaire = (lotCost?.mat ?? 0) + (lotCost?.mo ?? 0);

    const lotNumero: string = fl?.lot?.numero ?? "";
    const rawDate: any = fl?.facture?.date ?? fl?.date ?? null;
    const dateStr: string = rawDate ? new Date(rawDate).toISOString() : "";

    total += montantHT;
    clientMap.set(
      clientDenomination,
      (clientMap.get(clientDenomination) ?? 0) + montantHT,
    );
    articleMap.set(articleName, (articleMap.get(articleName) ?? 0) + montantHT);
    familleMap.set(familleName, (familleMap.get(familleName) ?? 0) + montantHT);
    groupeTiersMap.set(
      "—",
      (groupeTiersMap.get("—") ?? 0) + montantHT,
    );

    details.push({
      tiersDenomination: clientDenomination,
      tiersGroupe: "—",
      articleDenomination: articleName,
      familleDenomination: familleName,
      date: dateStr,
      lotId: lotId ?? undefined,
      lotNumero,
      quantite: qty,
      quantiteUG: qtyUG,
      prixUnitaire: pu,
      coutUnitaire,
      uniteVente,
      uniteGestion,
      montantHT,
      coutMateriel,
      coutMO,
    });
  }

  const toArray = (m: Map<string, number>) =>
    [...m.entries()].map(([denomination, t]) => ({ denomination, total: t }));

  return {
    byClient: toArray(clientMap),
    byArticle: toArray(articleMap),
    byFamille: toArray(familleMap),
    byGroupeTiers: toArray(groupeTiersMap),
    details,
    total,
  };
}
